import { useEffect, useRef, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

export function useWebSocket({ onNotification, enabled = true, token = null }) {
  const [connected, setConnected] = useState(false);
  const onNotificationRef = useRef(onNotification);
  onNotificationRef.current = onNotification;

  useEffect(() => {
    setConnected(false);
    if (!enabled || !token) return;
    let disposed = false;
    let socket = null;
    let timer = null;
    let attempts = 0;

    const connect = () => {
      if (disposed) return;
      const ws = new WebSocket(`${API.replace(/^http/, 'ws')}/api/ws/notifications?token=${token}`);
      socket = ws;
      ws.onopen = () => {
        if (disposed || socket !== ws) { ws.close(1000, 'View closed'); return; }
        setConnected(true);
        attempts = 0;
      };
      ws.onmessage = (event) => {
        if (disposed || socket !== ws) return;
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'notification') onNotificationRef.current?.(msg.data);
          if (msg.type === 'heartbeat' && ws.readyState === WebSocket.OPEN) ws.send('ping');
        } catch {}
      };
      ws.onclose = (event) => {
        if (disposed || socket !== ws) return;
        socket = null;
        setConnected(false);
        if (event.code === 4001 || attempts >= 10) return;
        const delay = Math.min(1000 * (2 ** attempts), 30000);
        attempts += 1;
        timer = setTimeout(connect, delay);
      };
      ws.onerror = () => {}; // onclose handles genuine transport failures.
    };

    // StrictMode's setup/cleanup cycle must not start a disposable handshake.
    timer = setTimeout(connect, 0);
    return () => {
      disposed = true;
      clearTimeout(timer);
      if (!socket) return;
      const pending = socket;
      pending.onmessage = null;
      pending.onclose = null;
      pending.onerror = null;
      if (pending.readyState === WebSocket.CONNECTING) {
        // Let the handshake finish, then close; aborting CONNECTING sockets
        // produces browser warnings and used to schedule orphan reconnects.
        pending.onopen = () => pending.close(1000, 'View closed');
      } else if (pending.readyState === WebSocket.OPEN) {
        pending.onopen = null;
        pending.close(1000, 'View closed');
      }
      socket = null;
    };
  }, [enabled, token]);

  return { connected };
}